import React from 'react';
import PropTypes from 'prop-types';
import { defaultBaseline, defaultFonts, safeRest } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';
import { IconArrowDown, IconArrowUp } from '@wds/icons';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/** Resolves the correct color */


var getMarkedColor = function getMarkedColor(color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }

  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }

  return palette[color].main;
};

var useStyles = createUseStyles(function (theme) {
  var palette = theme.palette,
      _theme$type = theme.type,
      fontSize = _theme$type.fontSize,
      fontWeight = _theme$type.fontWeight,
      border = theme.border,
      spacing = theme.spacing;
  var table = {
    thTypeColor: palette.background.dark,
    thBgColor: palette.transparent,
    thDividerLineColor: palette.transparent,
    tdTypeColor: palette.text.dark,
    tdBgColor: palette.transparent,
    cellMinHeight: 50,
    cellPadding: [spacing[4], spacing[5]],
    fontSize: fontSize.body
  };

  var cellStyles = function cellStyles(numeric) {
    return {
      fontSize: table.fontSize,
      color: table.tdTypeColor,
      backgroundColor: table.tdBgColor,
      textAlign: numeric ? 'right' : 'left',
      padding: table.cellPadding,
      minHeight: table.cellMinHeight,
      verticalAlign: 'top',
      borderBottom: border.muted,
      '& > *': {
        verticalAlign: 'middle'
      }
    };
  };

  return {
    wrenchTableContainer: function wrenchTableContainer(_ref) {
      var xScroll = _ref.xScroll;
      return _objectSpread(_objectSpread({}, defaultBaseline), {}, {
        overflowX: xScroll && 'auto'
      });
    },
    wrenchTable: function wrenchTable() {
      return _objectSpread(_objectSpread({}, defaultFonts(theme)), {}, {
        width: '100%',
        borderCollapse: 'collapse'
      });
    },
    wrenchTableBody: function wrenchTableBody() {
      return {};
    },
    wrenchTableRow: function wrenchTableRow(_ref2) {
      var marked = _ref2.marked,
          color = _ref2.color,
          disabled = _ref2.disabled;
      return {
        opacity: disabled && '0.5',
        pointerEvents: disabled && 'none',
        '&:last-child': {
          '& > td': {
            borderBottom: 'none'
          }
        },
        '& > td': {
          '&:first-child': {
            position: marked && 'relative',
            '&:before': {
              content: marked && '""',
              position: marked && 'absolute',
              left: marked && '0',
              top: marked && '0',
              bottom: marked && '0',
              width: marked && spacing[1],
              background: marked && getMarkedColor(color, palette),
              borderRight: border.muted
            }
          }
        }
      };
    },
    wrenchTableCell: function wrenchTableCell(_ref3) {
      var numeric = _ref3.numeric,
          variant = _ref3.variant,
          textWrap = _ref3.textWrap;
      return _objectSpread(_objectSpread({}, cellStyles(numeric)), {}, {
        width: variant && '1%',
        paddingRight: variant === 'bulkActions' && '0',
        whiteSpace: textWrap && 'nowrap',
        // TODO: create Link component to replace this
        '& > a': {
          color: palette.text.link
        }
      });
    },
    wrenchTableHeader: function wrenchTableHeader() {
      return {};
    },
    wrenchTableHeaderCell: function wrenchTableHeaderCell(_ref4) {
      var numeric = _ref4.numeric,
          textWrap = _ref4.textWrap,
          marked = _ref4.marked,
          color = _ref4.color,
          isSortable = _ref4.isSortable,
          isSorted = _ref4.isSorted;
      return _objectSpread(_objectSpread({}, cellStyles(numeric)), {}, {
        fontWeight: fontWeight.medium,
        backgroundColor: table.thBgColor,
        color: table.thTypeColor,
        borderLeft: "1px solid ".concat(table.thDividerLineColor),
        position: marked && 'relative',
        cursor: isSortable && 'pointer',
        userSelect: isSortable && 'none',
        whiteSpace: textWrap && 'nowrap',
        '&:before': {
          content: marked && '""',
          position: marked && 'absolute',
          left: marked && '0',
          top: marked && '0',
          width: marked && '100%',
          height: marked && spacing[1],
          background: marked && getMarkedColor(color, palette),
          borderRight: marked && border.muted
        },
        '&:last-child:before': {
          borderRight: marked && 'none'
        },
        '&:first-child': {
          borderLeft: 'none'
        },
        '&:hover, &:focus': {
          '& > span': {
            opacity: 1,
            color: isSorted ? palette.text.dark : palette.text.muted
          }
        }
      });
    },
    wrenchTableSortIcon: function wrenchTableSortIcon(_ref5) {
      var isSorted = _ref5.isSorted;
      return {
        display: 'inline-flex',
        pointerEvents: 'none',
        width: '0.9em',
        height: '0.9em',
        marginLeft: spacing[2],
        marginTop: -spacing[1],
        opacity: isSorted ? '1' : '0',
        color: palette.text.dark
      };
    }
  };
});
var _excluded$6 = ["children"];

var TableHeader = function TableHeader(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$6);

  var theme = useTheme();
  var classes = useStyles({
    theme: theme
  });
  return /*#__PURE__*/React.createElement("thead", Object.assign({
    className: classes.wrenchTableHeader
  }, safeRest(rest)), children);
};

TableHeader.displayName = "TableHeader";
TableHeader.propTypes = {
  /**
   * The content of the component, normally `Table.Row`.
   */
  children: PropTypes.node.isRequired
};
TableHeader.defaultProps = {};
TableHeader.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TableHeader",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "The content of the component, normally `Table.Row`."
    }
  }
};
var _excluded$5 = ["children"];

var TableBody = function TableBody(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$5);

  var theme = useTheme();
  var classes = useStyles({
    theme: theme
  });
  return /*#__PURE__*/React.createElement("tbody", Object.assign({
    className: classes.wrenchTableBody
  }, safeRest(rest)), children);
};

TableBody.displayName = "TableBody";
TableBody.propTypes = {
  /**
   * The content of the component, normally `Table.Row`.
   */
  children: PropTypes.node.isRequired
};
TableBody.defaultProps = {};
TableBody.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TableBody",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "The content of the component, normally `Table.Row`."
    }
  }
};

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
var DATA_VIZ_THEMES = Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
var KEYS = Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
var MARK_COLORS = Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});
var _excluded$4 = ["children", "color", "marked", "disabled", "parent", "onClick"];

var TableRow = function TableRow(_ref) {
  var children = _ref.children,
      color = _ref.color,
      marked = _ref.marked,
      disabled = _ref.disabled,
      parent = _ref.parent,
      onClick = _ref.onClick,
      rest = _objectWithoutProperties(_ref, _excluded$4);

  var theme = useTheme();
  var classes = useStyles({
    color: color,
    marked: marked,
    disabled: disabled,
    parent: parent,
    onClick: onClick,
    theme: theme
  });
  return /*#__PURE__*/React.createElement("tr", Object.assign({
    onClick: onClick,
    className: classes.wrenchTableRow
  }, safeRest(rest)), children);
};

TableRow.displayName = "TableRow";
TableRow.propTypes = {
  /**
   * The content of the table, normally `Table.Cell`.
   */
  children: PropTypes.node.isRequired,
  parent: PropTypes.oneOf(['Table.Header', 'Table.Body', 'Table.Footer', null]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  color: PropTypes.oneOfType([PropTypes.oneOf(MARK_COLORS), PropTypes.oneOf(DATA_VIZ_THEMES)]),
  marked: PropTypes.bool
};
TableRow.defaultProps = {
  parent: undefined,
  disabled: false,
  onClick: function onClick() {
    return undefined;
  },
  color: undefined,
  marked: false
};
TableRow.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TableRow",
  "props": {
    "parent": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'Table.Header'",
          "computed": false
        }, {
          "value": "'Table.Body'",
          "computed": false
        }, {
          "value": "'Table.Footer'",
          "computed": false
        }, {
          "value": "null",
          "computed": false
        }]
      },
      "required": false,
      "description": ""
    },
    "disabled": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "defaultValue": {
        "value": "() => undefined",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "color": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "union",
        "value": [{
          "name": "enum",
          "computed": true,
          "value": "MARK_COLORS"
        }, {
          "name": "enum",
          "computed": true,
          "value": "DATA_VIZ_THEMES"
        }]
      },
      "required": false,
      "description": ""
    },
    "marked": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "The content of the table, normally `Table.Cell`."
    }
  }
};
var _excluded$3 = ["children", "numeric", "variant", "scope", "parent", "color", "marked", "textWrap"];

var TableCell = function TableCell(_ref) {
  var children = _ref.children,
      numeric = _ref.numeric,
      variant = _ref.variant,
      scopeProp = _ref.scope,
      parent = _ref.parent,
      color = _ref.color,
      marked = _ref.marked,
      textWrap = _ref.textWrap,
      rest = _objectWithoutProperties(_ref, _excluded$3);

  var theme = useTheme();
  var classes = useStyles({
    numeric: numeric,
    variant: variant,
    color: color,
    marked: marked,
    textWrap: textWrap,
    theme: theme
  });
  var isHeaderChild = parent === 'Table.Header';
  var Component = isHeaderChild ? 'th' : 'td';
  var scope = !scopeProp && isHeaderChild ? 'col' : scopeProp;
  return /*#__PURE__*/React.createElement(Component, Object.assign({
    className: classes.wrenchTableCell,
    scope: scope
  }, safeRest(rest)), children);
};

TableCell.displayName = "TableCell";
TableCell.propTypes = {
  /**
   * The table cell contents.
   */
  children: PropTypes.node,

  /**
   * Color variants for cell
   */
  color: PropTypes.oneOfType([PropTypes.oneOf(MARK_COLORS), PropTypes.oneOf(DATA_VIZ_THEMES)]),
  marked: PropTypes.bool,

  /**
   * If `true`, content will align to the right.
   */
  numeric: PropTypes.bool,

  /**
   * Specify the cell type.
   * By default, the Table.Head, Table.Body or Table.Footer parent component set the value.
   */
  parent: PropTypes.oneOf(['Table.Header', 'Table.Body', 'Table.Footer', null]),

  /**
   * Set scope attribute.
   */
  scope: PropTypes.string,

  /**
   * sets the padding of the cell based on purpose
   */
  variant: PropTypes.oneOf(['moreActions', 'bulkActions', null]),

  /**
   * If true, cell contents will be wrapped
   */
  textWrap: PropTypes.bool
};
TableCell.defaultProps = {
  children: undefined,
  color: undefined,
  marked: false,
  numeric: false,
  parent: undefined,
  scope: undefined,
  variant: undefined,
  textWrap: false
};
TableCell.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TableCell",
  "props": {
    "children": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "The table cell contents."
    },
    "color": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "union",
        "value": [{
          "name": "enum",
          "computed": true,
          "value": "MARK_COLORS"
        }, {
          "name": "enum",
          "computed": true,
          "value": "DATA_VIZ_THEMES"
        }]
      },
      "required": false,
      "description": "Color variants for cell"
    },
    "marked": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "numeric": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If `true`, content will align to the right."
    },
    "parent": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'Table.Header'",
          "computed": false
        }, {
          "value": "'Table.Body'",
          "computed": false
        }, {
          "value": "'Table.Footer'",
          "computed": false
        }, {
          "value": "null",
          "computed": false
        }]
      },
      "required": false,
      "description": "Specify the cell type.\nBy default, the Table.Head, Table.Body or Table.Footer parent component set the value."
    },
    "scope": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Set scope attribute."
    },
    "variant": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'moreActions'",
          "computed": false
        }, {
          "value": "'bulkActions'",
          "computed": false
        }, {
          "value": "null",
          "computed": false
        }]
      },
      "required": false,
      "description": "sets the padding of the cell based on purpose"
    },
    "textWrap": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, cell contents will be wrapped"
    }
  }
};
var _excluded$2 = ["children", "numeric", "variant", "scope", "parent", "color", "marked", "nowrap", "isSortable", "isSorted", "sortDirection", "textWrap", "labelSort", "onClick"];

var TableHeaderCell = function TableHeaderCell(_ref) {
  var children = _ref.children,
      numeric = _ref.numeric,
      variant = _ref.variant,
      scopeProp = _ref.scope,
      parent = _ref.parent,
      color = _ref.color,
      marked = _ref.marked;
  _ref.nowrap;

  var isSortable = _ref.isSortable,
      isSorted = _ref.isSorted,
      sortDirection = _ref.sortDirection,
      textWrap = _ref.textWrap,
      labelSort = _ref.labelSort,
      _onClick = _ref.onClick,
      rest = _objectWithoutProperties(_ref, _excluded$2);

  var theme = useTheme();
  var classes = useStyles({
    numeric: numeric,
    variant: variant,
    color: color,
    marked: marked,
    textWrap: textWrap,
    isSortable: isSortable,
    isSorted: isSorted,
    theme: theme
  });

  var handleKeyDown = function handleKeyDown(e) {
    if (isSortable && e.keyCode === KEYS.ENTER) {
      _onClick(e);
    }
  };

  var SortArrow = function SortArrow() {
    var sortIcon; // Return nothing if cell is not sortable

    if (!isSortable) return false; // Choose arrow

    if (sortDirection === 'descending') {
      sortIcon = /*#__PURE__*/React.createElement(IconArrowDown, {
        title: "Sort rows in descending order."
      });
    } else {
      sortIcon = /*#__PURE__*/React.createElement(IconArrowUp, {
        title: "Sort rows in descending order."
      });
    }

    return /*#__PURE__*/React.createElement("span", {
      className: classes.wrenchTableSortIcon
    }, sortIcon);
  };

  var isHeaderChild = parent === 'Table.Header';
  var Component = 'th';
  var scope = !scopeProp && isHeaderChild ? 'col' : scopeProp;
  return /*#__PURE__*/React.createElement(Component, Object.assign({
    className: classes.wrenchTableHeaderCell,
    scope: scope,
    tabIndex: 0,
    role: "columnheader",
    "aria-sort": sortDirection,
    onKeyDown: handleKeyDown,
    title: labelSort,
    onClick: function onClick(e) {
      return _onClick(e);
    }
  }, safeRest(rest)), children, isSortable ? /*#__PURE__*/React.createElement(SortArrow, null) : null);
};

TableHeaderCell.displayName = "TableHeaderCell";
TableHeaderCell.propTypes = {
  /**
   * The table cell contents.
   */
  children: PropTypes.node,
  color: PropTypes.oneOfType([PropTypes.oneOf(MARK_COLORS), PropTypes.oneOf(DATA_VIZ_THEMES)]),
  marked: PropTypes.bool,
  numeric: PropTypes.bool,
  parent: PropTypes.oneOf(['Table.Header', null]),
  scope: PropTypes.string,
  variant: PropTypes.oneOf(['moreActions', 'bulkActions', null]),
  nowrap: PropTypes.bool,
  isSortable: PropTypes.bool,
  isSorted: PropTypes.bool,
  sortDirection: PropTypes.oneOf(['ascending', 'descending', null]),
  textWrap: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  labelSort: PropTypes.node
};
TableHeaderCell.defaultProps = {
  children: undefined,
  color: undefined,
  marked: false,
  numeric: false,
  parent: undefined,
  scope: undefined,
  variant: undefined,
  nowrap: false,
  isSortable: false,
  isSorted: false,
  sortDirection: 'ascending',
  textWrap: false,
  labelSort: 'Sort'
};
TableHeaderCell.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TableHeaderCell",
  "props": {
    "children": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "The table cell contents."
    },
    "color": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "union",
        "value": [{
          "name": "enum",
          "computed": true,
          "value": "MARK_COLORS"
        }, {
          "name": "enum",
          "computed": true,
          "value": "DATA_VIZ_THEMES"
        }]
      },
      "required": false,
      "description": ""
    },
    "marked": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "numeric": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "parent": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'Table.Header'",
          "computed": false
        }, {
          "value": "null",
          "computed": false
        }]
      },
      "required": false,
      "description": ""
    },
    "scope": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "variant": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'moreActions'",
          "computed": false
        }, {
          "value": "'bulkActions'",
          "computed": false
        }, {
          "value": "null",
          "computed": false
        }]
      },
      "required": false,
      "description": ""
    },
    "nowrap": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "isSortable": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "isSorted": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "sortDirection": {
      "defaultValue": {
        "value": "'ascending'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'ascending'",
          "computed": false
        }, {
          "value": "'descending'",
          "computed": false
        }, {
          "value": "null",
          "computed": false
        }]
      },
      "required": false,
      "description": ""
    },
    "textWrap": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "labelSort": {
      "defaultValue": {
        "value": "'Sort'",
        "computed": false
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": true,
      "description": ""
    }
  }
};
var _excluded$1 = ["children"];

var TableFooter = function TableFooter(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var theme = useTheme();
  var classes = useStyles({
    theme: theme
  });
  return /*#__PURE__*/React.createElement("tfoot", Object.assign({
    className: classes.wrenchTableBody
  }, safeRest(rest)), children);
};

TableFooter.displayName = "TableFooter";
TableFooter.propTypes = {
  /**
   * The content of the component, normally `Table.Row`.
   */
  children: PropTypes.node.isRequired
};
TableFooter.defaultProps = {};
TableFooter.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TableFooter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "The content of the component, normally `Table.Row`."
    }
  }
};
var _excluded = ["children", "xScroll"];

var Table = function Table(_ref) {
  var children = _ref.children,
      xScroll = _ref.xScroll,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();
  var classes = useStyles({
    xScroll: xScroll,
    theme: theme
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes.wrenchTableContainer
  }, /*#__PURE__*/React.createElement("table", Object.assign({
    className: classes.wrenchTable
  }, safeRest(rest)), children));
};

Table.displayName = "Table";
Table.propTypes = {
  /**
   * The content of the table, normally `Table.Header` and `Table.Body`.
   */
  children: PropTypes.node.isRequired,

  /**
   * Allows the table to scroll on its x-axis.
   */
  xScroll: PropTypes.bool
};
Table.defaultProps = {
  xScroll: false
};
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.HeaderCell = TableHeaderCell;
Table.Footer = TableFooter;
Table.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Table",
  "props": {
    "xScroll": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Allows the table to scroll on its x-axis."
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "The content of the table, normally `Table.Header` and `Table.Body`."
    }
  }
};
export { Table };